import { useState, useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Button,
  TextField,
  Card,
  CardContent,
  IconButton,
  Breadcrumbs,
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import {
  saveGameSettings,
  saveKnowledgeBaseItems,
  loadKbVideo,
  loadKbImage,
} from "../../redux/asyncThunk/userActions";
import { userSlice } from "../../redux/user/userSlice";
import InputFileUpload from "../UploadFile";
import { sanitizePlainText } from "../../utils/inputValidation";
import { getRootTopics, normalizeKbData } from "../../utils/kbTopics";
import {
  getProjectStatusFromSettings,
  settingsFromProjectStatus,
  mergeGameSettings,
} from "../../utils/projectAccess";
import { readChatAccessGateFromSettings } from "../../utils/chatAccessGate";
import { readAvailableMessengersFromSettings } from "../ProjectMessengerAvailability";
import "./mobile-editor.css";

const { addMessage, showDialog } = userSlice;

function createEmptyItem() {
  return {
    id: `item-${Date.now()}`,
    topicId: null,
    title: "",
    content: "",
    hasVideo: false,
    videopath: "",
    hasImage: false,
    imagepath: "",
  };
}

function createEmptyTopic(parentId = null) {
  return {
    id: `topic-${Date.now()}`,
    parentId,
    title: "",
  };
}

export default function MobileKnowledgeBaseEditor({ game: initialGame }) {
  const dispatch = useDispatch();
  const game = useSelector((state) => state.user.game) ?? initialGame;

  const [title, setTitle] = useState(game?.title ?? "");
  const [description, setDescription] = useState(game?.description ?? "");

  const kbData = normalizeKbData(game);
  const [topics, setTopics] = useState(kbData.topics || []);
  const [items, setItems] = useState(kbData.items || []);

  const [currentTopicId, setCurrentTopicId] = useState(null);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [savingItems, setSavingItems] = useState(false);

  const itemsSaveTimerRef = useRef(null);
  const lastSavedRef = useRef(null);

  // Получить текущий топик
  const currentTopic = currentTopicId
    ? topics.find((t) => t.id === currentTopicId)
    : null;

  // Получить элементы текущего топика или все если нет выбранного топика
  const topicItems = currentTopic
    ? items.filter((item) => item.topicId === currentTopic.id)
    : items;

  const currentItem = topicItems[currentItemIndex] || null;

  // Получить подтопики текущего топика
  const subTopics = currentTopic
    ? topics.filter((t) => t.parentId === currentTopic.id)
    : getRootTopics(topics);

  // Автосохранение
  useEffect(() => {
    if (!game?._id) return;

    const data = JSON.stringify({ topics, items });
    if (data === lastSavedRef.current) return;

    clearTimeout(itemsSaveTimerRef.current);
    itemsSaveTimerRef.current = setTimeout(() => {
      setSavingItems(true);
      dispatch(
        saveKnowledgeBaseItems({
          gameId: game._id,
          knowledgeBase: { topics, items },
        }),
      )
        .unwrap()
        .then(() => {
          lastSavedRef.current = data;
          setSavingItems(false);
        })
        .catch(() => {
          dispatch(addMessage({ type: "error", message: "Ошибка сохранения" }));
          setSavingItems(false);
        });
    }, 1500);

    return () => clearTimeout(itemsSaveTimerRef.current);
  }, [topics, items, game?._id, dispatch]);

  const updateItem = useCallback(
    (updates) => {
      setItems((prevItems) =>
        prevItems.map((item, idx) =>
          idx === currentItemIndex ? { ...item, ...updates } : item,
        ),
      );
    },
    [currentItemIndex],
  );

  const handleAddItem = () => {
    const newItem = createEmptyItem();
    if (currentTopic) {
      newItem.topicId = currentTopic.id;
    }
    setItems([...items, newItem]);
    setCurrentItemIndex(topicItems.length);
  };

  const handleDeleteItem = () => {
    if (topicItems.length <= 1) {
      dispatch(
        addMessage({
          type: "error",
          message: "Должен быть минимум один элемент",
        }),
      );
      return;
    }

    const itemToDelete = topicItems[currentItemIndex];
    setItems((prevItems) =>
      prevItems.filter((item) => item.id !== itemToDelete.id),
    );

    if (currentItemIndex > topicItems.length - 2) {
      setCurrentItemIndex(Math.max(0, topicItems.length - 2));
    }

    dispatch(addMessage({ type: "info", message: "Элемент удален" }));
  };

  const handleAddTopic = () => {
    const newTopic = createEmptyTopic(currentTopic?.id || null);
    setTopics([...topics, newTopic]);
  };

  const handleEnterTopic = (topicId) => {
    setCurrentTopicId(topicId);
    setCurrentItemIndex(0);
  };

  const handleBackTopic = () => {
    if (currentTopic?.parentId) {
      setCurrentTopicId(currentTopic.parentId);
    } else {
      setCurrentTopicId(null);
    }
    setCurrentItemIndex(0);
  };

  const handleVideoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentItem || !game?._id) return;

    setIsLoadingVideo(true);
    try {
      const result = await dispatch(
        loadKbVideo({
          gameId: game._id,
          itemId: currentItem.id,
          video: file,
        }),
      ).unwrap();

      updateItem({
        hasVideo: true,
        videopath: result.videopath,
      });

      dispatch(addMessage({ type: "info", message: "Видео загружено" }));
    } catch {
      dispatch(addMessage({ type: "error", message: "Ошибка загрузки видео" }));
    } finally {
      setIsLoadingVideo(false);
    }
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !currentItem || !game?._id) return;

    setIsLoadingImage(true);
    try {
      const result = await dispatch(
        loadKbImage({
          gameId: game._id,
          itemId: currentItem.id,
          image: file,
        }),
      ).unwrap();

      updateItem({
        hasImage: true,
        imagepath: result.imagepath,
      });

      dispatch(addMessage({ type: "info", message: "Изображение загружено" }));
    } catch {
      dispatch(
        addMessage({ type: "error", message: "Ошибка загрузки изображения" }),
      );
    } finally {
      setIsLoadingImage(false);
    }
  };

  if (!currentItem) {
    return (
      <div className="mobile-editor__empty">
        <p>Нет элементов для редактирования</p>
        <Button variant="contained" onClick={handleAddItem}>
          Добавить элемент
        </Button>
      </div>
    );
  }

  return (
    <div className="mobile-editor mobile-editor--kb">
      {/* Навигация по темам */}
      <div className="mobile-editor__breadcrumb">
        {currentTopic && (
          <IconButton size="small" onClick={handleBackTopic}>
            <KeyboardArrowLeftIcon />
          </IconButton>
        )}
        <Breadcrumbs maxItems={2} aria-label="breadcrumb">
          <span className="mobile-editor__breadcrumb-item">
            {currentTopic ? currentTopic.title : "База знаний"}
          </span>
        </Breadcrumbs>
      </div>

      {/* Редактор элемента */}
      <Card className="mobile-editor__card">
        <CardContent className="mobile-editor__item-content">
          <TextField
            fullWidth
            label="Заголовок"
            value={currentItem.title}
            onChange={(e) =>
              updateItem({ title: sanitizePlainText(e.target.value) })
            }
            className="mobile-editor__field"
            margin="normal"
          />

          <TextField
            fullWidth
            multiline
            rows={4}
            label="Содержание"
            value={currentItem.content}
            onChange={(e) => updateItem({ content: e.target.value })}
            className="mobile-editor__field"
            margin="normal"
          />

          {/* Видео */}
          {currentItem.hasVideo ? (
            <div className="mobile-editor__video-preview">
              <div className="mobile-editor__video-placeholder">
                <VideocamOutlinedIcon sx={{ fontSize: 48 }} />
                <p>Видео загружено</p>
              </div>
              <Button
                size="small"
                startIcon={<VideocamOutlinedIcon />}
                onClick={() => handleVideoUpload}
              >
                Изменить видео
              </Button>
            </div>
          ) : (
            <InputFileUpload
              onFileSelect={handleVideoUpload}
              accept="video/*"
              disabled={isLoadingVideo}
            />
          )}

          {/* Изображение */}
          {currentItem.hasImage ? (
            <div className="mobile-editor__image-preview">
              <div className="mobile-editor__image-placeholder">
                <ImageOutlinedIcon sx={{ fontSize: 48 }} />
                <p>Изображение загружено</p>
              </div>
              <Button
                size="small"
                startIcon={<ImageOutlinedIcon />}
                onClick={() => handleImageUpload}
              >
                Изменить изображение
              </Button>
            </div>
          ) : (
            <InputFileUpload
              onFileSelect={handleImageUpload}
              accept="image/*"
              disabled={isLoadingImage}
            />
          )}
        </CardContent>
      </Card>

      {/* Управление элементами */}
      <div className="mobile-editor__items-actions">
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddItem}
        >
          Добавить элемент
        </Button>
        {topicItems.length > 1 && (
          <Button
            fullWidth
            variant="outlined"
            color="error"
            startIcon={<DeleteOutlineIcon />}
            onClick={handleDeleteItem}
          >
            Удалить элемент
          </Button>
        )}
      </div>

      {/* Подтемы */}
      {subTopics.length > 0 && (
        <div className="mobile-editor__subtopics-section">
          <h3 className="mobile-editor__section-title">
            Подтемы ({subTopics.length})
          </h3>
          {subTopics.map((child) => {
            const childItems = items.filter(
              (item) => item.topicId === child.id,
            );
            return (
              <Card
                key={child.id}
                className="mobile-editor__topic-item"
                onClick={() => handleEnterTopic(child.id)}
              >
                <CardContent className="mobile-editor__topic-content">
                  <FolderOutlinedIcon className="mobile-editor__topic-icon" />
                  <div className="mobile-editor__topic-info">
                    <p className="mobile-editor__topic-title">
                      {child.title || "Без названия"}
                    </p>
                    <p className="mobile-editor__topic-count">
                      {childItems.length}{" "}
                      {childItems.length === 1 ? "элемент" : "элементов"}
                    </p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Добавить подтему */}
      <Button
        fullWidth
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddTopic}
        className="mobile-editor__btn-add-topic"
      >
        Добавить подтему
      </Button>
    </div>
  );
}
